import type { Row } from "./types.ts";
import { ISO_DATE, isoWeek, parseAmount } from "./format.ts";

export type ParsedInvoice = {
  line: number;
  dueDate: string;
  supplier: string;
  number: string;
  amount: number;
};

export type ExistingRow = Pick<Row, "id" | "dueDate" | "supplier" | "amount">;

export type ImportPlan = {
  add: Row[];
  duplicates: { row: Row; existing: ExistingRow; changed: string[] }[];
  duplicatesInFile: Row[];
  ignoredLines: number[];
};

const ID_PREFIX = "r_inv_";

const COLUMNS = {
  dueDate: ["Förfallodatum"],
  supplier: ["Leverantör"],
  number: ["Nummer", "Fakturanummer"],
  amount: ["Belopp (inkl. moms): Värde", "Totalt belopp", "Belopp"],
};

export function parseCsvLine(line: string, delim: string): string[] {
  const out: string[] = [];
  let field = "";
  let inQuotes = false;
  for (let i = 0; i < line.length; i++) {
    const c = line[i];
    if (inQuotes) {
      if (c === '"') {
        if (line[i + 1] === '"') {
          field += '"';
          i++;
        } else inQuotes = false;
      } else field += c;
    } else if (c === '"') inQuotes = true;
    else if (c === delim) {
      out.push(field);
      field = "";
    } else field += c;
  }
  out.push(field);
  return out.map((f) => f.trim());
}

export function detectDelimiter(headerLine: string): string {
  return [";", "\t", ","].sort(
    (a, b) => headerLine.split(b).length - headerLine.split(a).length,
  )[0]!;
}

function findColumn(header: string[], names: string[]): number {
  for (const name of names) {
    const i = header.indexOf(name.toLowerCase());
    if (i !== -1) return i;
  }
  return -1;
}

export function parseInvoiceCsv(text: string): {
  invoices: ParsedInvoice[];
  ignoredLines: number[];
} {
  const lines = text
    .replace(/^\uFEFF/, "")
    .split(/\r?\n/)
    .map((l, i) => ({ line: i + 1, text: l }))
    .filter((l) => l.text.trim() !== "");
  if (lines.length < 2) throw new Error("Filen innehåller inga fakturarader");

  const delim = detectDelimiter(lines[0]!.text);
  const header = parseCsvLine(lines[0]!.text, delim).map((h) =>
    h.toLowerCase(),
  );
  const required = (key: "dueDate" | "supplier" | "amount") => {
    const i = findColumn(header, COLUMNS[key]);
    if (i === -1) {
      throw new Error(`Kolumn saknas i filen: ${COLUMNS[key][0]}`);
    }
    return i;
  };
  const iDate = required("dueDate");
  const iSupplier = required("supplier");
  const iAmount = required("amount");
  const iNumber = findColumn(header, COLUMNS.number);

  const invoices: ParsedInvoice[] = [];
  const ignoredLines: number[] = [];
  for (const { line, text: raw } of lines.slice(1)) {
    const f = parseCsvLine(raw, delim);
    const dueDate = f[iDate] ?? "";
    const supplier = f[iSupplier] ?? "";
    if (dueDate === "" && supplier === "") {
      ignoredLines.push(line);
      continue;
    }
    if (!ISO_DATE.test(dueDate)) {
      throw new Error(
        `Rad ${line}: ogiltigt datum "${dueDate}" (väntade ÅÅÅÅ-MM-DD)`,
      );
    }
    if (supplier === "") throw new Error(`Rad ${line}: leverantör saknas`);
    const amount = parseAmount(f[iAmount] ?? "");
    if (!Number.isFinite(amount)) {
      throw new Error(`Rad ${line}: ogiltigt belopp "${f[iAmount] ?? ""}"`);
    }
    invoices.push({
      line,
      dueDate,
      supplier,
      number: iNumber === -1 ? "" : (f[iNumber] ?? ""),
      amount,
    });
  }
  return { invoices, ignoredLines };
}

export function normalizeKeyPart(s: string): string {
  return s.trim().toLowerCase().replace(/\s+/g, " ");
}

export function invoiceKey(inv: {
  supplier: string;
  number: string;
  dueDate: string;
  amount: number;
}): string {
  const parts = inv.number
    ? [inv.supplier, inv.number]
    : [inv.supplier, inv.dueDate, String(inv.amount)];
  return parts.map(normalizeKeyPart).join("|");
}

export async function invoiceId(key: string): Promise<string> {
  const digest = await crypto.subtle.digest(
    "SHA-256",
    new TextEncoder().encode(key),
  );
  const hex = Array.from(new Uint8Array(digest), (b) =>
    b.toString(16).padStart(2, "0"),
  ).join("");
  return ID_PREFIX + hex.slice(0, 16);
}

export async function toImportRow(inv: ParsedInvoice): Promise<Row> {
  return {
    id: await invoiceId(invoiceKey(inv)),
    week: isoWeek(inv.dueDate),
    dueDate: inv.dueDate,
    supplier: inv.number ? `${inv.supplier}, ${inv.number}` : inv.supplier,
    amount: -Math.abs(inv.amount),
    note: "",
    snoozed: false,
  };
}

export async function planImport(
  invoices: ParsedInvoice[],
  existing: ExistingRow[],
  ignoredLines: number[] = [],
): Promise<ImportPlan> {
  const byId = new Map(existing.map((r) => [r.id, r]));
  const bySupplier = new Map(
    existing.map((r) => [normalizeKeyPart(r.supplier), r]),
  );
  const seen = new Set<string>();
  const plan: ImportPlan = {
    add: [],
    duplicates: [],
    duplicatesInFile: [],
    ignoredLines,
  };

  for (const inv of invoices) {
    const row = await toImportRow(inv);
    if (seen.has(row.id)) {
      plan.duplicatesInFile.push(row);
      continue;
    }
    seen.add(row.id);

    const match =
      byId.get(row.id) ?? bySupplier.get(normalizeKeyPart(row.supplier));
    if (match) {
      const changed: string[] = [];
      if (match.dueDate !== row.dueDate) {
        changed.push(`förfallodatum ${match.dueDate} → ${row.dueDate}`);
      }
      if (match.amount !== row.amount) {
        changed.push(`belopp ${match.amount} → ${row.amount}`);
      }
      plan.duplicates.push({ row, existing: match, changed });
      continue;
    }
    plan.add.push(row);
  }
  return plan;
}

export async function planImportFromText(
  text: string,
  existing: ExistingRow[],
): Promise<ImportPlan> {
  const { invoices, ignoredLines } = parseInvoiceCsv(text);
  return planImport(invoices, existing, ignoredLines);
}
